'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useToastActions } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { FileXls, Envelope, Lock, ArrowRight, GoogleLogo, User, Globe, Users } from '@phosphor-icons/react';
import { authApi } from '../services/api';
import { teamApi } from '../api/team';
import { shareApi } from '../services/shareApi';
import { Footer } from './components/Footer';
import { useTeamStore } from '../stores/teamStore';
import { useLanguageStore, SUPPORTED_LANGUAGES } from '../stores/languageStore';
import { getRegionProfile } from '../config/region';
import i18n, { storeCodeToLocale } from '../i18n';
import * as Dialog from '@radix-ui/react-dialog';

// Magnetic Button Component
function MagneticButton({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      <Button {...props}>{children}</Button>
    </motion.div>
  );
}

// Google OAuth Button
function GoogleButton({ onClick, isLoading }: { onClick: () => void; isLoading: boolean }) {
  const { t } = useTranslation();
  
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      ) : (
        <GoogleLogo className="w-5 h-5" weight="fill" />
      )}
      <span className="font-medium">{t('auth.loginWithGoogle')}</span>
    </motion.button>
  );
}

// Language Selector Component
function LanguageSelector() {
  const { language, setLanguage } = useLanguageStore();
  const profile = getRegionProfile();
  const [isOpen, setIsOpen] = useState(false);

  // cn 区域不支持语言切换
  if (profile.i18nMode === 'single') {
    return null;
  }

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (code: string) => {
    setLanguage(code as typeof language);
    const locale = storeCodeToLocale(code);
    i18n.changeLanguage(locale);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.name}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-lg border border-zinc-200 py-1 z-50"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                  language === lang.code
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-zinc-700 hover:bg-zinc-50'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
              </button>
            ))}
          </motion.div>
        </>
      )}
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const { setCurrentTeamId } = useTeamStore();
  const profile = getRegionProfile();
  const isCN = profile.region === 'cn';

  // Get invite token from URL
  const inviteToken = searchParams.get('invite_token');
  void inviteToken;

  // Step 1: Email input (for non-CN region)
  const [email, setEmail] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Step 2: Code verification (for non-CN region)
  const [step, setStep] = useState<'email' | 'code' | 'password'>('email');
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Step 3: Set password (for new users in non-CN region)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [userId, setUserId] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  // Common
  const [error, setError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // CN Region: Username/Password login
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isPasswordLoggingIn, setIsPasswordLoggingIn] = useState(false);

  // 邀请对话框状态
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [pendingInviteInfo, setPendingInviteInfo] = useState<{
    token: string;
    teamName: string;
    role: string;
  } | null>(null);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);

  // Countdown timer (for non-CN region)
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      setError(t('auth.invalidEmail'));
      return;
    }

    setError('');
    setIsSendingCode(true);

    try {
      const response = await authApi.sendLoginCode(email);
      if (response.code === 0) {
        setStep('code');
        setCountdown(60);
      } else {
        setError(response.message || t('auth.failedToSendCode'));
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.failedToSendCode');
      setError(errorMsg);
    } finally {
      setIsSendingCode(false);
    }
  };

  // 检查localStorage中是否有待处理的邀请
  const checkPendingInvite = async () => {
    const pendingToken = localStorage.getItem('pending_invite_token');
    if (pendingToken) {
      try {
        const response = await teamApi.getInvitationInfo(pendingToken);
        if (response.code === 0 && response.data) {
          setPendingInviteInfo({
            token: pendingToken,
            teamName: response.data.team.name,
            role: response.data.role,
          });
          setShowInviteDialog(true);
          return true;
        } else {
          // 邀请无效或已过期，清除localStorage
          localStorage.removeItem('pending_invite_token');
        }
      } catch (err: unknown) {
        // 检查错误类型，只有邀请真正无效时才清除localStorage
        const error = err as { response?: { status?: number; data?: { code?: number; message?: string } } };
        const statusCode = error.response?.status;
        const errorCode = error.response?.data?.code;
        
        // 只有邀请不存在或已过期时才清除，网络错误保留token让用户可以重试
        if (statusCode === 404 || errorCode === 404 || errorCode === 1003) {
          localStorage.removeItem('pending_invite_token');
        }
        // 其他错误（如网络错误）保留token，不显示邀请对话框
      }
    }
    return false;
  };

  // 处理登录成功后的逻辑
  const handleLoginSuccess = async (
    userData: {
      id: string;
      username: string;
      email?: string;
      nickname?: string;
      role?: string;
      ownedTeamId?: string;
    },
    accessToken: string
  ) => {
    // 执行登录
    login(userData, accessToken);

    // 获取用户的团队列表；默认个人模式（currentTeamId 为空）
    try {
      const teamsResponse = await teamApi.listTeams();
      if (teamsResponse.code === 0 && teamsResponse.data) {
        setCurrentTeamId(teamsResponse.data.currentTeamId ?? "");
      } else {
        setCurrentTeamId("");
      }
    } catch (err) {
      setCurrentTeamId("");
    }

    // 检查是否有待处理的邀请（来自localStorage）
    const hasPendingInvite = await checkPendingInvite();
    if (hasPendingInvite) {
      // 有邀请，等待用户确认，不跳转
      return;
    }

    // 检查是否有待复制的分享工作流（来自localStorage）
    const pendingShareCopy = localStorage.getItem('pending_share_copy');
    if (pendingShareCopy) {
      try {
        const { shareToken, workflowName } = JSON.parse(pendingShareCopy);
        if (shareToken) {
          // 清除localStorage中的待复制信息
          localStorage.removeItem('pending_share_copy');
          // 复制工作流
          const response = await shareApi.copyWorkflow(shareToken, {
            newName: `${workflowName || '工作流'} (复制)`,
          });

          console.log('copy workflow response', response, `/workflows/store/edit/${response.data?.id}`);

          if (response.code === 0 && response.data?.id) {
            // 显示成功提示
            toastSuccess(t('share.header.copySuccess') || '复制成功');
            // 跳转到工作流编辑页
            navigate('/workflows', {replace: true});
            // navigate(`/workflows/store/edit/${response.data?.id}`, { replace: true });
            return;
          } else {
            toastError(response.message || t('share.copyFailed') || '复制失败');
          }
        }
      } catch (err) {
        // 复制失败，清除localStorage并继续跳转到首页
        localStorage.removeItem('pending_share_copy');
        toastError(t('share.copyFailed') || '复制失败');
      }
    }

    // 跳转到首页
    navigate('/home', { replace: true });
  };

  const { success: toastSuccess, error: toastError } = useToastActions();

  // 处理接受邀请
  const handleAcceptInvite = async () => {
    if (!pendingInviteInfo) return;
    
    setIsAcceptingInvite(true);
    try {
      const response = await teamApi.acceptInvitation(pendingInviteInfo.token);
      if (response.code === 0 && response.data?.teamId) {
        // 更新当前团队为新加入的团队
        setCurrentTeamId(response.data.teamId);
        // 清除localStorage中的邀请信息
        localStorage.removeItem('pending_invite_token');
        // 关闭对话框并跳转
        setShowInviteDialog(false);
        // 显示成功提示
        toastSuccess(t('teamInvite.acceptSuccess') || '加入团队成功');
        // 跳转到首页
        navigate('/home/store', { replace: true });
      } else {
        // 加入失败，显示错误提示并跳转
        toastError(response.message || t('teamInvite.acceptFailed'));
        localStorage.removeItem('pending_invite_token');
        setShowInviteDialog(false);
        navigate('/home/store', { replace: true });
      }
    } catch (err) {
      // 加入失败，显示错误提示并跳转
      toastError(t('teamInvite.acceptError') || '加入团队失败，请联系管理员');
      localStorage.removeItem('pending_invite_token');
      setShowInviteDialog(false);
      navigate('/home/store', { replace: true });
    } finally {
      setIsAcceptingInvite(false);
    }
  };

  // 处理拒绝邀请（跳过）
  const handleSkipInvite = () => {
    // 清除localStorage中的邀请信息
    localStorage.removeItem('pending_invite_token');
    setShowInviteDialog(false);
    navigate('/home', { replace: true });
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      setError(t('auth.invalidCode'));
      return;
    }

    setError('');
    setIsVerifying(true);

    try {
      const response = await authApi.verifyLoginCode(email, code);
      if (response.code === 0) {
        const data = response.data;
        if (data.requires_password) {
          setUserId(data.user_id || '');
          setStep('password');
        } else if (data.tokens && data.user) {
          // Direct login
          await handleLoginSuccess(
            {
              id: data.user.id,
              username: data.user.username,
              email: data.user.email,
              nickname: data.user.nickname,
              role: data.user.role,
              ownedTeamId: data.user.ownedTeamId,
            },
            data.tokens.access_token
          );
        }
      } else {
        setError(response.message || t('auth.invalidCode'));
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.invalidCode');
      setError(errorMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSetPassword = async () => {
    if (!nickname || nickname.trim().length < 2) {
      setError(t('auth.nicknameTooShort'));
      return;
    }

    if (!password || password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    // Validate password complexity
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const complexity = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;

    if (complexity < 3) {
      setError(t('auth.passwordComplexity'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setError('');
    setIsSettingPassword(true);

    try {
      const response = await authApi.setPasswordAndLogin(userId, password, nickname.trim());
      if (response.code === 0) {
        const data = response.data;
        await handleLoginSuccess(
          {
            id: data.user.id,
            username: data.user.username,
            email: data.user.email,
            nickname: data.user.nickname,
            role: data.user.role,
          },
          data.tokens.access_token
        );
      } else {
        setError(response.message || t('auth.failedToSetPassword'));
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.failedToSetPassword');
      setError(errorMsg);
    } finally {
      setIsSettingPassword(false);
    }
  };

  // CN Region: Username/Password login
  const handlePasswordLogin = async () => {
    if (!loginUsername || loginUsername.length < 3) {
      setError(t('auth.usernameTooShort'));
      return;
    }
    if (!loginPassword) {
      setError(t('auth.passwordRequired'));
      return;
    }

    setError('');
    setIsPasswordLoggingIn(true);

    try {
      const response = await authApi.login(loginUsername, loginPassword);
      if (response.code === 0) {
        const { user, tokens } = response.data;
        await handleLoginSuccess(
          {
            id: user.id,
            username: user.username,
            email: user.email,
            nickname: user.nickname,
            role: user.role,
          },
          tokens.access_token
        );
      } else {
        setError(response.message || t('auth.invalidCredentials'));
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.invalidCredentials');
      setError(errorMsg);
    } finally {
      setIsPasswordLoggingIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError('');

    try {
      // 从后端获取 Google 授权 URL
      const response = await authApi.getGoogleAuthURL();
      
      if (response.code === 0 && response.data) {
        // 保存 state 用于回调验证
        sessionStorage.setItem('google_state', response.data.state);
        // 跳转到 Google 授权页面
        window.location.href = response.data.auth_url;
      } else {
        setError(response.message || t('auth.googleLoginFailed'));
        setIsGoogleLoading(false);
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.googleLoginFailed');
      setError(errorMsg);
      setIsGoogleLoading(false);
    }
  };

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 20,
      },
    },
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex">
      {/* Left Side - Branding */}
      <motion.div
        className="hidden lg:flex lg:w-1/2 bg-zinc-900 relative overflow-hidden"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        {/* Floating Elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-zinc-700/30 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-8">
              <FileXls className="w-5 h-5 text-emerald-400" weight="fill" />
              <span className="text-sm font-medium text-white/90">{t('app.name')}</span>
            </div>

            <h1 className="text-5xl xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              {t('auth.loginTitle')}
              <br />
              <span className="text-emerald-400">{t('auth.loginSubtitle')}</span>
            </h1>

            <p className="text-lg text-zinc-400 max-w-md leading-relaxed">{t('auth.loginDescription')}</p>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="flex gap-12 mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div>
              <div className="text-3xl font-bold text-white">10x</div>
              <div className="text-sm text-zinc-500 mt-1">{t('auth.efficiencyGain')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">15+</div>
              <div className="text-sm text-zinc-500 mt-1">{t('auth.builtInOperators')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">0</div>
              <div className="text-sm text-zinc-500 mt-1">{t('auth.codeWriting')}</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col p-6 lg:p-12">
        {/* Header with Language Selector */}
        <div className="flex justify-end mb-4">
          <LanguageSelector />
        </div>

        <div className="flex-1 flex items-center justify-center">
        <motion.div className="w-full max-w-md" variants={containerVariants} initial="hidden" animate="visible">
          {/* Mobile Logo */}
          <motion.div variants={itemVariants} className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-900 mb-4 overflow-hidden">
              <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">{t('app.name')}</h1>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card variant="elevated" className="overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight">
                    {isCN 
                      ? t('auth.welcomeBack')
                      : (
                        <>
                          {step === 'email' && t('auth.welcomeBack')}
                          {step === 'code' && t('auth.enterVerificationCode')}
                          {step === 'password' && t('auth.setPassword')}
                        </>
                      )}
                  </h2>
                  <p className="text-zinc-500 mt-2 text-sm">
                    {isCN
                      ? t('auth.enterCredentials')
                      : (
                        <>
                          {step === 'email' && t('auth.enterEmailToContinue')}
                          {step === 'code' && t('auth.codeSentTo', { email })}
                          {step === 'password' && t('auth.setPasswordToContinue')}
                        </>
                      )}
                  </p>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                  >
                    {error}
                  </motion.div>
                )}

                {/* CN Region: Username/Password Login */}
                {isCN ? (
                  <div className="space-y-5">
                    <Input
                      label={t('auth.username')}
                      type="text"
                      value={loginUsername}
                      onChange={(e) => setLoginUsername(e.target.value)}
                      leftIcon={<User className="w-5 h-5" />}
                      placeholder={t('auth.placeholderUsername')}
                      required
                    />

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-sm font-medium text-zinc-700">{t('auth.password')}</label>
                        <Link to="/forgot-password" className="text-xs text-emerald-600 hover:text-emerald-700">
                          {t('auth.forgotPassword')}
                        </Link>
                      </div>
                      <Input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        leftIcon={<Lock className="w-5 h-5" />}
                        placeholder={t('auth.placeholderPassword')}
                        required
                      />
                    </div>

                    <div className="pt-2">
                      <MagneticButton
                        onClick={handlePasswordLogin}
                        variant="primary"
                        size="lg"
                        isLoading={isPasswordLoggingIn}
                        className="w-full"
                        rightIcon={<ArrowRight className="w-5 h-5" />}
                      >
                        {t('auth.login')}
                      </MagneticButton>
                    </div>

                    {/* Register Link */}
                    <div className="mt-6 pt-6 border-t border-zinc-100 text-center">
                      <p className="text-sm text-zinc-500">
                        {t('auth.noAccount')}{' '}
                        <Link to="/register" className="text-emerald-600 hover:text-emerald-700 font-medium">
                          {t('auth.registerNow')}
                        </Link>
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Step 1: Email Input */}
                    {step === 'email' && (
                      <div className="space-y-5">
                        <Input
                          label={t('auth.email')}
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          leftIcon={<Envelope className="w-5 h-5" />}
                          placeholder={t('auth.placeholderEmail')}
                          required
                        />

                        <div className="pt-2">
                          <MagneticButton
                            onClick={handleSendCode}
                            variant="primary"
                            size="lg"
                            isLoading={isSendingCode}
                            className="w-full"
                            rightIcon={<ArrowRight className="w-5 h-5" />}
                          >
                            {t('auth.continue')}
                          </MagneticButton>
                        </div>

                        {/* Divider */}
                        <div className="relative my-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-zinc-200" />
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white text-zinc-400">{t('auth.or')}</span>
                          </div>
                        </div>

                        {/* Google Login */}
                        <GoogleButton onClick={handleGoogleLogin} isLoading={isGoogleLoading} />
                      </div>
                    )}

                {/* Step 2: Code Verification */}
                {step === 'code' && (
                  <div className="space-y-5">
                    <Input
                      label={t('auth.verificationCode')}
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      leftIcon={<Lock className="w-5 h-5" />}
                      placeholder={t('auth.placeholderCode')}
                      maxLength={6}
                      required
                    />

                    <div className="pt-2">
                      <MagneticButton
                        onClick={handleVerifyCode}
                        variant="primary"
                        size="lg"
                        isLoading={isVerifying}
                        className="w-full"
                        rightIcon={<ArrowRight className="w-5 h-5" />}
                      >
                        {t('auth.verify')}
                      </MagneticButton>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => setStep('email')}
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        {t('auth.changeEmail')}
                      </button>
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={countdown > 0 || isSendingCode}
                        className="text-emerald-600 hover:text-emerald-700 disabled:text-zinc-400"
                      >
                        {countdown > 0 ? t('auth.resendCodeIn', { seconds: countdown }) : t('auth.resendCode')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Set Password */}
                {step === 'password' && (
                  <div className="space-y-5">
                    <Input
                      label={t('auth.nickname')}
                      type="text"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      leftIcon={<User className="w-5 h-5" />}
                      placeholder={t('auth.placeholderNickname')}
                      required
                    />

                    <div>
                      <Input
                        label={t('auth.password')}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        leftIcon={<Lock className="w-5 h-5" />}
                        placeholder={t('auth.placeholderPassword')}
                        required
                      />
                      <p className="text-xs text-zinc-500 mt-1">{t('auth.passwordRequirements')}</p>
                    </div>

                    <Input
                      label={t('auth.confirmPassword')}
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      leftIcon={<Lock className="w-5 h-5" />}
                      placeholder={t('auth.placeholderConfirmPassword')}
                      required
                    />

                    <div className="pt-2">
                      <MagneticButton
                        onClick={handleSetPassword}
                        variant="primary"
                        size="lg"
                        isLoading={isSettingPassword}
                        className="w-full"
                        rightIcon={<ArrowRight className="w-5 h-5" />}
                      >
                        {t('auth.completeLogin')}
                      </MagneticButton>
                    </div>
                  </div>
                )}
              </>
            )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer */}
          <Footer />
        </motion.div>
        </div>
      </div>

      {/* 邀请确认对话框 */}
      <Dialog.Root open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <Dialog.Portal>
          <AnimatePresence>
            {showInviteDialog && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <div className="text-center">
                      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-indigo-500" />
                      </div>
                      <Dialog.Title className="text-xl font-semibold text-zinc-900 mb-2">
                        {t('teamInvite.title')}
                      </Dialog.Title>
                      <Dialog.Description className="text-zinc-500 mb-6">
                        {t('teamInvite.pendingInviteDesc', { 
                          team: pendingInviteInfo?.teamName,
                          role: pendingInviteInfo?.role === 'admin' ? t('team.roleAdmin') : t('team.roleMember')
                        }) || `您被邀请加入 "${pendingInviteInfo?.teamName}" 团队，担任 ${pendingInviteInfo?.role === 'admin' ? t('team.roleAdmin') : t('team.roleMember')}。是否接受邀请？`}
                      </Dialog.Description>

                      <div className="flex gap-3">
                        <Button
                          variant="secondary"
                          onClick={handleSkipInvite}
                          className="flex-1"
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button
                          onClick={handleAcceptInvite}
                          isLoading={isAcceptingInvite}
                          className="flex-1"
                        >
                          {t('teamInvite.acceptAndJoin')}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
